import { useEffect, useLayoutEffect, useState } from 'react';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Custom hook to detect media queries and handle responsive layouts
 * @param query - Media query string (e.g., '(min-width: 768px)')
 * @returns boolean - true if media query matches, false otherwise
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    // Avoid SSR issues by checking if window is defined
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useIsomorphicLayoutEffect(() => {
    // Avoid SSR issues by checking if window/matchMedia is defined
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    const update = () => {
      setMatches(mediaQuery.matches);
    };

    // Set initial state immediately
    update();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', update);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', update);
      }
    };
  }, [query]);

  return matches;
};
